/**
 * One panel's fault stays in that panel — G3, applied to a render rather than a read.
 *
 * G3 *a panel failed to load* already decides this for a GET that did not answer: *"that panel only … Every
 * other panel stays."* A panel that throws while RENDERING has exactly the same consequence for the reader and
 * had none of the same protection: one bad field in C4's ladder took the whole console down to React Router's
 * error boundary, so the strip went with it — and B1 is the one thing on the page that must never be taken
 * down by something below it.
 *
 * So the rule is the same rule, and it is the named failure mode that justifies this existing at all: a panel
 * is where a panel's trouble is said.
 *
 * It is a class because an error boundary can only be a class; there is no hook for this.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  /** The panel this guards, for the `data-panel` the audit and the reader both look for. */
  panelKey: string;
  title: string;
  children: ReactNode;
}

interface State {
  broke: Error | null;
}

export class PanelBoundary extends Component<Props, State> {
  state: State = { broke: null };

  static getDerivedStateFromError(error: Error): State {
    return { broke: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged, not swallowed. The sentence on screen is for the reader; this is for whoever has to fix it, and
    // without it a panel would simply be missing with no way to find out why.
    // eslint-disable-next-line no-console
    console.error(`panel ${this.props.panelKey} failed to render`, error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.broke) return this.props.children;
    return (
      <section className="panel" data-panel={this.props.panelKey} data-broke="">
        <header className="panel__head">
          <h2 className="panel__title">{this.props.title}</h2>
        </header>
        {/* The browser's own sentence, and it says so plainly: this is not the server's failure, and the
            reader should not go looking at the server for it. */}
        <p className="rowalarm rowalarm--red">
          <b>this panel couldn't be drawn</b>
          <span>
            The data arrived and the console couldn't render it. Everything else on this page is unaffected,
            and the figures here are the only ones missing.
          </span>
        </p>
      </section>
    );
  }
}
